// Hook for subscribing to agent action events and refreshing UI data
import { useEffect, useRef } from 'react';

// Event types for agent actions
export type AgentRefreshEvent = {
  type: 'agent:action_completed';
  detail: {
    actionType: string;
    success: boolean;
    invalidateQueries: string[];
    entityType?: 'todo' | 'lead';
    entityId?: string;
  };
};

// Custom event name
export const AGENT_REFRESH_EVENT = 'agent:action_completed';

// Dispatch an agent refresh event
export function dispatchAgentRefresh(detail: AgentRefreshEvent['detail']) {
  const event = new CustomEvent(AGENT_REFRESH_EVENT, { detail });
  window.dispatchEvent(event);
}

// Hook to subscribe to agent refresh events
export function useAgentRefresh(
  queries: string[],
  onRefresh: () => void | Promise<void>
) {
  // Use refs to avoid re-registering event listeners on every render
  const queriesRef = useRef(queries);
  const onRefreshRef = useRef(onRefresh);

  // Update refs when values change
  useEffect(() => {
    queriesRef.current = queries;
    onRefreshRef.current = onRefresh;
  });

  useEffect(() => {
    const handleEvent = (event: Event) => {
      const customEvent = event as CustomEvent<AgentRefreshEvent['detail']>;
      const { invalidateQueries, success } = customEvent.detail;

      // Only refresh if action was successful and queries match
      if (success && invalidateQueries.some((q) => queriesRef.current.includes(q))) {
        onRefreshRef.current();
      }
    };

    window.addEventListener(AGENT_REFRESH_EVENT, handleEvent);
    return () => {
      window.removeEventListener(AGENT_REFRESH_EVENT, handleEvent);
    };
  }, []); // Empty deps - only register once
}

// Predefined query keys for consistency
export const QUERY_KEYS = {
  // Todos
  TODOS: 'todos:list',
  TODO_DETAIL: (id: string) => `todos:detail:${id}`,
  // Leads
  LEADS: 'leads:list',
  LEAD_DETAIL: (id: string) => `leads:detail:${id}`,
  // Calendar
  CALENDAR: 'calendar:events',
  CALENDAR_EVENT: (id: string) => `calendar:event:${id}`,
  // Forms
  FORMS: 'forms:list',
  FORM_DETAIL: (id: string) => `forms:detail:${id}`,
  // Site Pages
  SITE_PAGES: 'site:pages',
  SITE_PAGE: (type: string) => `site:page:${type}`,
  SITE_BLOCKS: (pageType: string) => `site:blocks:${pageType}`,
} as const;
